import { action, makeObservable, observable, reaction, runInAction } from 'mobx';
import { ulid } from 'ulid';
import type ft from 'firebase';
import * as Sentry from '@sentry/react';

import { CardDateEntry, FirestoreCard } from '@creative-kit/shared';
import type { RootStore } from '.';
import { Card } from './card';
import { normalizeCardDates } from './cardDateNormalization';
import { db } from './data';

export class CardsStore {
  private stopListeningToCards = () => {};
  private rootStore: RootStore;
  /**
   * Queue to ensure snapshots are processed sequentially.
   *
   * Race Condition Scenario:
   * When a Firestore snapshot arrives, we process it and may trigger async migrations
   * (e.g., migrateLegacyDates). If the migration writes back to Firestore, it triggers
   * a new snapshot. Without queuing, the new snapshot could start processing before
   * the current snapshot's migration completes, leading to:
   * - Overlapping state updates
   * - Lost migrations (new snapshot sees old data before migration completes)
   * - Inconsistent card state
   *
   * Solution: Chain snapshots via Promise.then() so each snapshot waits for the
   * previous one (including all async migrations) to fully complete.
   */
  private snapshotQueue: Promise<void> = Promise.resolve();
  cards: Record<string, Card> = {};
  initialLoadComplete = false;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      cards: observable,
      initialLoadComplete: observable,
      updateCards: action.bound,
      resetCards: action,
    });

    this.rootStore = rootStore;
  }

  init() {
    reaction(() => this.rootStore.sessionsStore.currentSession, this.watchCards, {
      fireImmediately: true,
    });
  }

  watchCards = () => {
    const { currentSession } = this.rootStore.sessionsStore;
    this.stopListeningToCards();
    this.resetCards();
    // Reset queue when switching sessions to avoid processing stale snapshots
    this.snapshotQueue = Promise.resolve();
    if (currentSession) {
      this.stopListeningToCards = db
        .sessionCards(currentSession.id)
        .onSnapshot(this.enqueueSnapshot.bind(this, currentSession.id), (error) => {
          // eslint-disable-next-line no-console
          console.error('[CardsStore] Cards listener error:', error.code, error.message);
        });
    }
  };

  /**
   * Enqueues a snapshot for sequential processing.
   * This wrapper ensures updateCards completes (including async migrations)
   * before the next snapshot begins processing.
   */
  private enqueueSnapshot(sessionId: string, snapshot: ft.firestore.QuerySnapshot<FirestoreCard>) {
    this.snapshotQueue = this.snapshotQueue.then(() => this.updateCards(sessionId, snapshot));
  }

  resetCards() {
    for (const key of Object.keys(this.cards)) {
      delete this.cards[key];
    }
    this.initialLoadComplete = false;
  }

  async updateCards(sessionId: string, snapshot: ft.firestore.QuerySnapshot<FirestoreCard>) {
    for (const change of snapshot.docChanges()) {
      const changeType = change.type;
      const changeDoc = change.doc;
      const cardData = changeDoc.data();
      const normalizedCardData = normalizeCardDates(cardData);
      // eslint-disable-next-line no-await-in-loop
      const migratedCardData = await this.migrateLegacyDates(
        sessionId,
        changeDoc.id,
        normalizedCardData,
      );

      // Wrap mutations in runInAction since async actions lose context after await
      runInAction(() => {
        if (changeType === 'added') {
          // Only add cards that aren't deleted
          if (!migratedCardData.deletedAt) {
            this.cards[changeDoc.id] = new Card(
              changeDoc.id,
              sessionId,
              migratedCardData,
              this.rootStore,
            );
          }
        } else if (change.type === 'modified') {
          if (migratedCardData.deletedAt) {
            // Card was deleted, remove from active cards
            delete this.cards[changeDoc.id];
          } else if (this.cards[changeDoc.id]) {
            // Card exists and wasn't deleted, update it
            this.cards[changeDoc.id].updateData(migratedCardData);
          } else {
            // Card was restored, add it back
            this.cards[changeDoc.id] = new Card(
              changeDoc.id,
              sessionId,
              migratedCardData,
              this.rootStore,
            );
          }
        } else {
          delete this.cards[changeDoc.id];
        }
      });
    }

    // Wrap final mutation in runInAction as well
    runInAction(() => {
      this.rootStore.uiCardsStore.initializeCards(Object.values(this.cards));
      if (!this.initialLoadComplete) {
        this.initialLoadComplete = true;
      }
    });
  }

  private async migrateLegacyDates(
    sessionId: string,
    cardId: string,
    cardData: FirestoreCard,
  ): Promise<FirestoreCard> {
    if (cardData.dates && cardData.dates.length > 0) {
      return cardData;
    }

    const legacyStartDate = (cardData as { startDate?: Date }).startDate;
    const legacyEndDate = (cardData as { endDate?: Date }).endDate;

    if (!legacyStartDate && !legacyEndDate) {
      return cardData;
    }

    const migratedDates: CardDateEntry[] = [];

    if (legacyStartDate) {
      migratedDates.push({
        id: ulid(),
        type: 'delivery',
        startDate: legacyStartDate,
        startHasTime: true,
        endHasTime: false,
        isTentative: false,
      });
    }

    if (legacyEndDate) {
      migratedDates.push({
        id: ulid(),
        type: 'publish', // Migrating legacy endDate to publish
        endDate: legacyEndDate,
        startHasTime: false,
        endHasTime: true,
        isTentative: false,
      });
    }

    if (migratedDates.length > 0) {
      try {
        await db.sessionCards(sessionId).doc(cardId).update({
          dates: migratedDates,
        });
      } catch (error) {
        // Log detailed context for debugging
        const errorContext = {
          sessionId,
          cardId,
          legacyStartDate: (cardData as { startDate?: Date }).startDate?.toISOString(),
          legacyEndDate: (cardData as { endDate?: Date }).endDate?.toISOString(),
          migratedDatesCount: migratedDates.length,
          error: error instanceof Error ? error.message : String(error),
        };

        // eslint-disable-next-line no-console
        console.error('Failed to migrate legacy card dates:', errorContext, error);

        // Report to Sentry with full context
        Sentry.captureException(error, {
          tags: {
            component: 'CardsStore',
            operation: 'migrateLegacyDates',
          },
          contexts: {
            migration: errorContext,
          },
        });

        // Show user-friendly error (non-blocking - card still loads with un-migrated data)
        this.rootStore.uiSnackbarStore.setSnackbarDetails({
          description: 'Failed to migrate legacy dates. Card loaded with original dates.',
        });

        // Return un-migrated data so card still loads
        // Note: Migration will retry on next snapshot if card is updated
        return cardData;
      }
    }

    return {
      ...cardData,
      dates: migratedDates,
    };
  }
}
